import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { characterPath, type BookEntry } from "../config/site";
import type { CharacterPage } from "../data/character-pages";
import { loadMinorIds, loadPortraitUrls } from "../data/load-book-home";

const SHOWN_FIRST = 10;

interface Tile extends CharacterPage {
  imageUrl: string | undefined;
}

async function loadTiles(bookId: string): Promise<Tile[]> {
  const [response, urlOf, minor] = await Promise.all([
    fetch(`/book-data/${bookId}/character-pages.json`),
    loadPortraitUrls(bookId),
    loadMinorIds(bookId),
  ]);
  const { characters } = await response.json() as { characters: CharacterPage[] };
  return characters
    .filter((character) => !minor.has(character.id))
    .map((character) => ({ ...character, imageUrl: urlOf(character.id) }));
}

/** A book's characters, most seen first. The top ten show, and the rest open from a button. */
export function BookCharacters({ book }: { book: BookEntry }) {
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    void loadTiles(book.id).then(setTiles).catch(() => setTiles([]));
  }, [book.id]);

  if (tiles.length === 0) {
    return null;
  }
  const hidden = tiles.length - SHOWN_FIRST;
  return (
    <nav className="character-tiles" aria-labelledby="character-tiles-title">
      <h2 id="character-tiles-title" className="book-section-title">Characters</h2>
      <ul className="character-tiles__list">
        {(showAll ? tiles : tiles.slice(0, SHOWN_FIRST)).map((tile) => (
          <li key={tile.id}>
            <Link className="character-tile" to={characterPath(book, tile.id)}>
              {tile.imageUrl === undefined
                ? <span className="character-tile__face" aria-hidden="true" />
                : <img className="character-tile__face" src={tile.imageUrl} alt="" loading="lazy" />}
              <span className="character-tile__name">{tile.displayName}</span>
              <span className="character-tile__scenes">
                {tile.scenes} {tile.scenes === 1 ? "scene" : "scenes"}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {hidden > 0 ? (
        <button
          type="button"
          className="character-tiles__more"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "Show fewer" : `Show ${hidden} more`}
        </button>
      ) : null}
    </nav>
  );
}
